import React from 'react';
import {
  TrendingUp, TrendingDown, PiggyBank,
  Target, BookOpen, ArrowUpRight, ArrowDownRight,
  Activity, MoreHorizontal
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import '../styles/dashboard.css';

// --- Dummy data ---
const spendingData = [
  { month: 'Oct', income: 4200, expenses: 2800 },
  { month: 'Nov', income: 4500, expenses: 3100 },
  { month: 'Dec', income: 5200, expenses: 4200 },
  { month: 'Jan', income: 4800, expenses: 2900 },
  { month: 'Feb', income: 4600, expenses: 3200 },
  { month: 'Mar', income: 5100, expenses: 3400 },
  { month: 'Apr', income: 5400, expenses: 3100 },
];

const categoryData = [
  { name: 'Housing', value: 35, color: '#E8471E' },
  { name: 'Food', value: 20, color: '#C8941A' },
  { name: 'Transport', value: 15, color: '#4a9eff' },
  { name: 'Entertainment', value: 12, color: '#7c5cbf' },
  { name: 'Savings', value: 18, color: '#3ab86c' },
];

const recentTransactions = [
  { id: 1, name: 'Salary Deposit', category: 'Income', amount: +5400, date: 'Apr 20', type: 'credit' },
  { id: 2, name: 'Grocery Store', category: 'Food', amount: -124.50, date: 'Apr 19', type: 'debit' },
  { id: 3, name: 'Electric Bill', category: 'Utilities', amount: -89.00, date: 'Apr 18', type: 'debit' },
  { id: 4, name: 'Netflix', category: 'Entertainment', amount: -15.99, date: 'Apr 17', type: 'debit' },
  { id: 5, name: 'Freelance Payment', category: 'Income', amount: +750.00, date: 'Apr 16', type: 'credit' },
];

const statCards = [
  { label: 'Monthly Income', value: '$5,400', change: '+8.2%', up: true, icon: TrendingUp, color: '#3ab86c' },
  { label: 'Monthly Expenses', value: '$3,100', change: '-3.5%', up: false, icon: TrendingDown, color: '#E8471E' },
  { label: 'Net Savings', value: '$2,300', change: '+12.4%', up: true, icon: PiggyBank, color: '#C8941A' },
  { label: 'Savings Goals', value: '2 Active', change: '68% avg', up: true, icon: Target, color: '#4a9eff' },
];

// Custom tooltip for chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="chart-tooltip">
        <p className="tooltip-label">{label}</p>
        {payload.map((p) => (
          <p key={p.name} style={{ color: p.color }}>
            {p.name}: <strong>${p.value.toLocaleString()}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const DashboardHome = () => {
  const { user } = useAuth();
  const displayName = user?.user_metadata?.full_name?.split(' ')[0] || 'there';
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="dash-page">
      {/* Header */}
      <div className="dash-header">
        <div>
          <h1 className="dash-greeting">{greeting}, {displayName} 👋</h1>
          <p className="dash-date">
            {now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="dash-alpha-badge">Alpha Preview</div>
      </div>

      {/* Stat cards */}
      <div className="stat-grid">
        {statCards.map(({ label, value, change, up, icon: Icon, color }) => (
          <div key={label} className="stat-card">
            <div className="stat-card-top">
              <span className="stat-label">{label}</span>
              <div className="stat-icon" style={{ background: `${color}18`, color }}>
                <Icon size={16} />
              </div>
            </div>
            <p className="stat-value">{value}</p>
            <div className={`stat-change ${up ? 'stat-up' : 'stat-down'}`}>
              {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {change} vs last month
            </div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="charts-grid">
        {/* Area chart */}
        <div className="chart-card chart-card-main">
          <div className="chart-card-header">
            <div>
              <h3 className="chart-title">Income vs Expenses</h3>
              <p className="chart-subtitle">Last 7 months overview</p>
            </div>
            <button className="chart-more-btn"><MoreHorizontal size={16} /></button>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={spendingData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3ab86c" stopOpacity={0.25}/>
                  <stop offset="95%" stopColor="#3ab86c" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="expenseGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E8471E" stopOpacity={0.25}/>
                  <stop offset="95%" stopColor="#E8471E" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e1e1e" />
              <XAxis dataKey="month" tick={{ fill: '#666', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#666', fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="income" name="Income" stroke="#3ab86c" fill="url(#incomeGrad)" strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#E8471E" fill="url(#expenseGrad)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Pie chart */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div>
              <h3 className="chart-title">Spending by Category</h3>
              <p className="chart-subtitle">This month</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={categoryData} cx="50%" cy="50%" innerRadius={52} outerRadius={78}
                paddingAngle={3} dataKey="value">
                {categoryData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value}%`, '']} contentStyle={{ background: '#161616', border: '1px solid #2a2a2a', borderRadius: 8, color: '#f0ede8' }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="pie-legend">
            {categoryData.map(({ name, value, color }) => (
              <div key={name} className="legend-item">
                <span className="legend-dot" style={{ background: color }} />
                <span className="legend-name">{name}</span>
                <span className="legend-value">{value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent transactions + Quick actions */}
      <div className="bottom-grid">
        {/* Transactions */}
        <div className="chart-card">
          <div className="chart-card-header">
            <div>
              <h3 className="chart-title">Recent Transactions</h3>
              <p className="chart-subtitle">Your latest activity</p>
            </div>
            <button className="btn-view-all">View all</button>
          </div>
          <div className="transactions-list">
            {recentTransactions.map((tx) => (
              <div key={tx.id} className="transaction-row">
                <div className={`tx-icon ${tx.type === 'credit' ? 'tx-credit' : 'tx-debit'}`}>
                  {tx.type === 'credit' ? <ArrowUpRight size={15} /> : <ArrowDownRight size={15} />}
                </div>
                <div className="tx-info">
                  <p className="tx-name">{tx.name}</p>
                  <p className="tx-category">{tx.category} · {tx.date}</p>
                </div>
                <span className={`tx-amount ${tx.type === 'credit' ? 'tx-amount-credit' : 'tx-amount-debit'}`}>
                  {tx.type === 'credit' ? '+' : ''}${Math.abs(tx.amount).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick actions */}
        <div className="quick-actions-card">
          <h3 className="chart-title" style={{ marginBottom: '1rem' }}>Quick Actions</h3>
          <div className="quick-actions-list">
            {[
              { icon: PiggyBank, label: 'Add Expense', sub: 'Log a new transaction', color: '#E8471E', href: '/dashboard/budget' },
              { icon: Target, label: 'Set a Goal', sub: 'Create a savings target', color: '#C8941A', href: '/dashboard/goals' },
              { icon: BookOpen, label: 'Learn Finance', sub: 'Explore education modules', color: '#4a9eff', href: '/dashboard/learn' },
              { icon: Activity, label: 'View Reports', sub: 'Detailed analytics', color: '#3ab86c', href: '/dashboard/budget' },
            ].map(({ icon: Icon, label, sub, color, href }) => (
              <a key={label} href={href} className="quick-action-item">
                <div className="quick-action-icon" style={{ background: `${color}18`, color }}>
                  <Icon size={18} />
                </div>
                <div>
                  <p className="quick-action-label">{label}</p>
                  <p className="quick-action-sub">{sub}</p>
                </div>
              </a>
            ))}
          </div>

          {/* Coming soon banner */}
          <div className="coming-soon-banner">
            <span>🚀</span>
            <div>
              <p className="coming-soon-title">More features coming soon</p>
              <p className="coming-soon-sub">Budget Tracker & Education modules are next</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
